import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { Gift, Trophy, ChevronRight, Users, Clock, Check, Sparkles } from "lucide-react";

const API = window.location.origin;

const GiveawayLobbyBanner = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [giveaway, setGiveaway] = useState(null);

  useEffect(() => {
    if (!token) return;
    const fetchActive = async () => {
      try {
        const { data } = await axios.get(`${API}/api/giveaways`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const active = (Array.isArray(data) ? data : []).find(
          (g) => g.status === "active"
        );
        if (active) setGiveaway(active);
      } catch {}
    };
    fetchActive();
  }, [token]);

  if (!giveaway) return null;

  const endDate = giveaway.end_date
    ? new Date(giveaway.end_date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border cursor-pointer group transition-all hover:scale-[1.005]"
      style={{
        borderColor: "rgba(77,124,255,0.2)",
        background: "linear-gradient(135deg, rgba(77,124,255,0.06) 0%, rgba(0,0,0,0.5) 100%)",
      }}
      onClick={() => navigate("/giveaways")}
      data-testid="giveaway-lobby-banner"
    >
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at left, rgba(77,124,255,0.1), transparent 60%)",
        }}
      />

      <div className="relative flex flex-col sm:flex-row items-center gap-4 p-4 sm:p-5">
        {/* Image */}
        {giveaway.image_url && (
          <div className="flex-shrink-0 w-full sm:w-32 h-32 sm:h-24 rounded-xl overflow-hidden bg-black/30">
            <img
              src={giveaway.image_url}
              alt={giveaway.title}
              className="w-full h-full object-contain"
            />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-1.5">
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/30">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider">
                Live Giveaway
              </span>
            </div>
            {giveaway.entry_count > 0 && (
              <span className="flex items-center gap-1 text-[10px] text-zinc-400">
                <Users className="w-3 h-3" />
                {giveaway.entry_count.toLocaleString()} entered
              </span>
            )}
          </div>

          <h3 className="text-base sm:text-lg font-bold text-white mb-1 truncate">
            {giveaway.title}
          </h3>

          {giveaway.prize && (
            <div className="flex items-center justify-center sm:justify-start gap-1.5 mb-2">
              <Trophy className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-sm font-semibold text-amber-400">
                {giveaway.prize}
              </span>
            </div>
          )}

          <div className="flex items-center justify-center sm:justify-start gap-3 text-[11px] text-zinc-500">
            {endDate && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> Ends {endDate}
              </span>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="flex-shrink-0">
          {giveaway.user_entered ? (
            <div className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30">
              <Check className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-bold text-emerald-400">
                Entered
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-5 py-2.5 rounded-full font-bold text-sm text-white transition-all group-hover:scale-105" style={{ background: '#4d7cff', boxShadow: '0 0 16px rgba(77,124,255,0.3)' }}>
              <Gift className="w-4 h-4" />
              Enter Now
              <ChevronRight className="w-4 h-4" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiveawayLobbyBanner;
